/*
  Theme: the announcement bar pause toggle.

  ⚠️ WHAT WAS ACTUALLY WRONG, because it was not a broken toggle.

  There was NO toggle. Before this file there was no JavaScript on this bar at
  all, no click handler, no class, no data attribute and no aria-pressed.
  Horizon ships assets/announcement-bar.js, but that binds to
  <announcement-bar-component>, which our bespoke bar does not use and which is
  not loaded on our pages.

  The ONLY pause mechanism in the whole theme was one CSS rule:

    .core-announce:hover, .core-announce:focus-within { animation-play-state: paused }

  On a touch device, tapping an element applies the browser's EMULATED hover,
  which matches that rule, so the bar stopped and it looked like a pause
  control. The emulated hover then STAYS APPLIED until the shopper taps
  somewhere else, so a second tap on the bar kept the same rule matching and
  nothing changed. The dead second tap was sticky :hover, not a handler that
  failed to fire.

  THE FIX IS TWO PARTS, and the CSS half matters as much as this file:

    1. Real state, in ONE place: `data-ats-paused` on the bar. Every tap flips
       that one attribute, so a second tap cannot no-op. The button's
       aria-pressed is written from the same value, so the two cannot drift.
    2. The hover rule is gated to `(hover: hover) and (pointer: fine)` in the
       section stylesheet, so a touch device never gets the sticky emulated
       hover in the first place and relies solely on this toggle. Mouse users
       keep hover-pause exactly as it was.

  ⚠️ HOVER-PAUSE AND TAP-TOGGLE ARE MUTUALLY EXCLUSIVE BY POINTER CAPABILITY,
  AND THAT SPLIT IS THE WHOLE POINT OF THE HOVER_POINTER CONSTANT BELOW.

  The first version of this file gated the CSS but left the handler pointer
  agnostic, so on a desktop mouse BOTH mechanisms were live at once. Measured at
  1440 before this fix: a click set data-ats-paused and aria-pressed, and moving
  the mouse away did NOT resume, because the attribute rule pauses independently
  of :hover. So one stray click parked the marquee permanently. Hover on its own
  had always been correct; the click was pure surplus.

  The rule now, and the two halves can never both apply:

    (hover: hover) and (pointer: fine)   hover pauses, unhover resumes,
    matches       (desktop mouse)        POINTER CLICKS ARE IGNORED
    does not      (touch, coarse)        tap toggles, no hover involved

  THE HANDLER TESTS THE SAME MEDIA QUERY STRING THE STYLESHEET USES. It is
  duplicated here because CSS cannot gate a listener and JS cannot read an
  @media block, so the two must be kept in step by hand. If the query in
  sections/core-announcement-bar.liquid ever changes, change HOVER_POINTER with
  it, or the store gets both behaviours again on some class of device.

  ⚠️ KEYBOARD IS NOT A POINTER, AND IS NEVER GATED. Enter and Space on a
  <button> dispatch a click with `detail === 0` and no pointer behind it, which
  is how this file tells a keypress from a mouse press. Gating on the media
  query alone would have taken the pause control away from every desktop
  keyboard user, which is exactly the population WCAG's pause requirement
  exists for. Touch derived clicks carry detail 1, so they do not take this
  path and cannot double toggle.

  ⚠️ ONE LISTENER, ON THE BAR, DELIBERATELY. The visually hidden button sits
  inside the bar, so a click on it bubbles to this same handler and toggles
  once. Binding a second listener to the button would toggle twice per press
  and land back where it started, which is the very bug being fixed. Keyboard
  Enter and Space on a <button> also dispatch click, so they route through here
  too and need no separate key handling.

  ⚠️ IT NEVER STARTS MOTION UNDER prefers-reduced-motion. That case is handled
  in assets/core-base.css with `animation: none !important` on the groups,
  which removes the animation outright rather than pausing it, so there is
  nothing for a play state to resume. The control is hidden in that mode too,
  because a pause button for motion that does not exist is a lie. This file
  additionally refuses to act, so the attribute cannot end up out of step with
  what is on screen.
*/
(function () {
  'use strict';

  var PAUSED = 'corePaused'; // data-ats-paused

  /* MUST MATCH the @media query wrapping the :hover rule in
     sections/core-announcement-bar.liquid, character for character. See the
     header note: this is the one capability split, expressed twice because CSS
     and JS cannot share it. */
  var HOVER_POINTER = '(hover: hover) and (pointer: fine)';

  function init(bar) {
    if (!bar || bar.dataset.announceReady === 'true') return;
    bar.dataset.announceReady = 'true';

    var button = bar.querySelector('[data-core-announce-toggle]');
    var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    var hoverPointer = window.matchMedia(HOVER_POINTER);

    function isPaused() {
      return bar.hasAttribute('data-core-paused');
    }

    function apply(paused) {
      if (paused) {
        bar.setAttribute('data-core-paused', '');
      } else {
        bar.removeAttribute('data-core-paused');
      }
      if (button) {
        button.setAttribute('aria-pressed', paused ? 'true' : 'false');
        button.textContent = paused ? button.dataset.labelPlay : button.dataset.labelPause;
      }
    }

    bar.addEventListener('click', function (event) {
      /* Nothing is moving, so there is nothing to pause or resume. Refusing
         here keeps the attribute honest rather than letting it claim a state
         the shopper cannot see. */
      if (reducedMotion.matches) return;

      /* Enter and Space on the button arrive as a click with detail 0. Those
         are always honoured, on every device, because a keyboard user has no
         hover to fall back on. Mouse and touch clicks carry a click count of
         at least 1. */
      var fromKeyboard = event.detail === 0;

      /* The pointer split. On a hover capable, fine pointer the stylesheet is
         already pausing on hover, so a click here would be a second, competing
         mechanism that sticks after the mouse leaves. Ignore it and let hover
         own the behaviour outright. Touch and other coarse pointers fall
         through and toggle exactly as before. */
      if (!fromKeyboard && hoverPointer.matches) return;

      apply(!isPaused());
    });

    /* Start from whatever the markup says, which is running. Written through
       apply() rather than assumed, so the button label and aria-pressed are
       correct on first paint without duplicating the mapping. */
    apply(isPaused());
  }

  function boot() {
    try {
      var bars = document.querySelectorAll('.core-announce');
      for (var i = 0; i < bars.length; i++) init(bars[i]);
    } catch (e) {
      /* A bar that fails to enhance still scrolls and still pauses on hover for
         mouse users. Never take the page down for this. */
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }
})();
